import type { Messages } from '../catalogue.en'

/**
 * Schedule: the day view and an appointment's lifecycle.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const schedule: Partial<Messages> = {
  'schedule.selfBooking.title': 'Самозапись пациентов',
  'schedule.selfBooking.doctor': 'Врач',
  'schedule.selfBooking.enabled': 'Разрешена',
  'schedule.selfBooking.disabled': 'Не разрешена',
  'schedule.selfBooking.allowDoctor': 'Разрешить самозапись к врачу {name}',
  'schedule.selfBooking.inactiveDoctor': 'Неактивный врач',
  'schedule.selfBooking.empty': 'Нет доступных врачей',
  'schedule.selfBooking.saving': 'Сохраняем разрешение',
  'schedule.selfBooking.saved': 'Разрешение сохранено',
  'schedule.selfBooking.stale': 'Разрешение изменилось. Обновите данные перед повторной попыткой.',
  'schedule.selfBooking.failed': 'Не удалось сохранить или обновить разрешение.',
  'schedule.selfBooking.reload': 'Обновить разрешения',
  'schedule.title': 'Расписание',
  'schedule.today': 'Сегодня',
  'schedule.invalidDate': 'Эта дата недействительна',
  'schedule.invalidDateDetail': 'Используйте элементы выбора даты, чтобы выбрать день.',
  'schedule.columnPatient': 'Пациент',
  'schedule.openPatient': 'Открыть {name}',
  'schedule.patientCode': 'Код пациента',
  'schedule.availability.available': 'Доступно',
  'schedule.availability.noAvailability': 'Нет доступного времени для записи',
  'schedule.resourceType.practitioner': 'Врач',
  'schedule.resourceType.patient': 'Пациент',
  'schedule.resourceType.room': 'Кабинет',
  'schedule.resourceType.equipment': 'Оборудование',
  'schedule.resourceType.location': 'Место',
  'schedule.resourceType.organization': 'Организация',
  'schedule.resourceType.servicePoint': 'Пункт обслуживания',
  'schedule.resourceType.other': 'Другое',
  'schedule.busyKind.appointment': 'Приём',
  'schedule.busyKind.hold': 'Резерв',
  'schedule.busyKind.block': 'Заблокированное время',
  'schedule.busyKind.busyElsewhere': 'Занят в другом месте',
  'schedule.lifecycle.held': 'Зарезервирован',
  'schedule.lifecycle.planned': 'Запланирован',
  'schedule.lifecycle.fulfilled': 'Состоялся',
  'schedule.lifecycle.cancelled': 'Отменён',
  'schedule.lifecycle.noShow': 'Не явился',
  'schedule.lifecycle.enteredInError': 'Внесён по ошибке',
  'schedule.dayStatus.planned': 'Предстоит',
  'schedule.dayStatus.started': 'Пришёл',
  'schedule.dayStatus.completed': 'Завершён',
  'schedule.dayStatus.cancelled': 'Отменён',
  'schedule.dayStatus.noShow': 'Не явился',
  'schedule.candidateReason.earlier': 'Более ранний вариант',
  'schedule.candidateReason.sameSite': 'Та же площадка',
  'schedule.candidateReason.preferredResource': 'Предпочтительный ресурс',
  'schedule.candidateReason.smallerPractitionerGap': 'Меньше окно у врача',
  'schedule.command.applied': 'Применено',
  'schedule.command.replayed': 'Уже применено',
  'schedule.command.overlap': 'Это время частично совпадает с другим пунктом расписания.',
  'schedule.command.capacityExceeded': 'У выбранного ресурса не осталось свободной ёмкости.',
  'schedule.command.outsideAvailability': 'Выбранное время находится за пределами доступного времени.',
  'schedule.command.revisionMismatch': 'Расписание изменилось, пока это действие было открыто.',
  'schedule.command.holdExpired': 'Срок этого резерва истёк.',
  'schedule.command.forbidden': 'У вас нет прав менять это расписание.',
  'schedule.command.invalidCommand': 'Это действие с расписанием больше недействительно.',
  'schedule.command.missing': 'Этой записи расписания больше нет. Обновите доску.',
  'schedule.command.rejected': 'Действие с расписанием отклонено.',
  'schedule.command.conflict': 'Расписание изменилось до того, как это действие было применено.',
  'schedule.command.failed': 'Не удалось выполнить действие с расписанием.',
  'schedule.command.patientUnavailable': 'В данный момент пациент недоступен.',
  'schedule.command.outsidePatientStay': 'Это время вне периода поездки пациента.',

  'schedule.nav.label': 'Разделы расписания',
  'schedule.nav.calendar': 'Календарь',
  'schedule.nav.calendarDescription': 'Планирование приёмов и заблокированного времени',
  'schedule.nav.availability': 'Доступность',
  'schedule.nav.availabilityDescription': 'Недельное расписание и изменения дат',
  'schedule.nav.resources': 'Ресурсы',
  'schedule.nav.resourcesDescription': 'Места, организации, внешние ресурсы',
  'schedule.nav.visitTypes': 'Типы приёмов',
  'schedule.nav.visit-typesDescription': 'Длительность и необходимые ресурсы',
  'schedule.find.action': 'Найти время',
  'schedule.block.action': 'Заблокировать время',
  'schedule.block.create': 'Создать блок',
  'schedule.appointment.action': 'Новый приём',
  'schedule.appointment.create': 'Создать приём',
  'schedule.appointment.visitReason': 'Причина визита',

  'schedule.settings.loading': 'Загрузка настроек расписания',
  'schedule.settings.loadFailed': 'Не удалось загрузить настройки расписания.',
  'schedule.settings.save': 'Сохранить',
  'schedule.settings.cancel': 'Отмена',
  'schedule.settings.saveFailed': 'Не удалось сохранить изменение.',
  'schedule.settings.editNamed': 'Изменить {name}',
  'schedule.settings.actions': 'Действия',
  'schedule.settings.active': 'Активен',
  'schedule.settings.inactive': 'Неактивен',
  'schedule.resources.title': 'Ресурсы',
  'schedule.resources.branch': 'Филиал',
  'schedule.branches.all': 'Все филиалы',
  'schedule.branches.workplaceRequired': 'Перед добавлением рабочих часов создайте врачу действующее место работы в этом филиале.',
  'schedule.command.workplaceRequired': 'Выберите место работы врача для этого приёма.',
  'schedule.command.workplaceUnavailable': 'Место работы недоступно для выбранного врача, филиала или времени приёма.',
  'schedule.command.workplaceChanged': 'Место работы изменилось. Обновите данные и подтвердите выбор.',
  'schedule.branches.overlap': 'Рабочие часы в разных филиалах пересекаются. Исправьте интервалы доступности.',
  'schedule.branches.globalBlock': 'Эти часы пересекаются с блокировкой во всех филиалах.',
  'schedule.branches.clearDescription': 'Очистить часы и изменения по датам только в этом филиале. Общие блокировки, личная занятость и другие филиалы останутся без изменений.',
  'schedule.resources.branchRequired': 'Выберите филиал',
  'schedule.resources.branchUnavailable': 'Этот филиал больше недоступен. Выберите другой филиал.',
  'schedule.resources.branchInUse': 'Перенесите или отмените записи с этим ресурсом, прежде чем менять его филиал.',
  'schedule.resources.subtitle': 'Именованные места, организации, оборудование и внешние специалисты.',
  'schedule.resources.name': 'Название',
  'schedule.resources.type': 'Тип',
  'schedule.resources.location': 'Место',
  'schedule.resources.grid': 'Сетка',
  'schedule.resources.state': 'Состояние',
  'schedule.resources.minutes': '{count} мин',
  'schedule.resources.preBuffer': 'Буфер до, мин',
  'schedule.resources.postBuffer': 'Буфер после, мин',
  'schedule.resources.capabilities': 'Возможности',
  'schedule.resources.addTitle': 'Добавить ресурс',
  'schedule.resources.editTitle': 'Изменить ресурс',
  'schedule.resources.detailsTitle': 'Сведения о ресурсе',
  'schedule.resources.openNamed': 'Открыть ресурс {name}',
  'schedule.resources.empty': 'Отдельные ресурсы не настроены.',
  'schedule.resources.availabilityDefaults': 'Доступность по умолчанию',
  'schedule.resources.address': 'Адрес',
  'schedule.resources.addressLine1': 'Адрес',
  'schedule.resources.addressLine2': 'Уточнение адреса',
  'schedule.resources.city': 'Город',
  'schedule.resources.region': 'Область или регион',
  'schedule.resources.postalCode': 'Почтовый индекс',
  'schedule.resources.country': 'Код страны',
  'schedule.resources.contact': 'Контакт',
  'schedule.resources.phone': 'Телефон',
  'schedule.resources.email': 'Электронная почта',
  'schedule.resources.notes': 'Примечания',
  'schedule.resources.metadata': 'Метаданные (JSON)',
  'schedule.resources.booking': 'Запись на приём',
  'schedule.availability.title': 'Доступность',
  'schedule.availability.subtitle': 'Профили доступности для врачей, пациентов и отдельных ресурсов.',
  'schedule.availability.resource': 'Ресурс',
  'schedule.availability.target': 'Цель',
  'schedule.availability.timeZone': 'Часовой пояс',
  'schedule.availability.useClinicTimeZone': 'Использовать часовой пояс клиники',
  'schedule.availability.useClinicCalendar': 'Использовать производственный календарь клиники',
  'schedule.availability.calendar': 'Рабочий календарь',
  'schedule.availability.calendarNone': 'Не применён',
  'schedule.availability.booking': 'Бронирование',
  'schedule.availability.bookable': 'Доступно для записи',
  'schedule.availability.openForBooking': 'Открыто для записи',
  'schedule.availability.informational': 'Информационный',
  'schedule.availability.profilesEmpty': 'Профили доступности не настроены.',
  'schedule.availability.openNamed': 'Открыть доступность {name}',
  'schedule.availability.appointment': 'Приём',
  'schedule.availability.busy': 'Другая занятость',
  'schedule.availability.hold': 'Зарезервировано (в ожидании)',
  'schedule.availability.dayAvailableHours': '{date}: доступно {hours}',
  'schedule.availability.dayClosed': '{date}: недоступно',
  'schedule.availability.add': 'Добавить',
  'schedule.availability.menuExtra': 'Добавить часы на эту дату',
  'schedule.availability.menuBlock': 'Отметить как недоступное',
  'schedule.availability.editDay': 'Изменить этот день',
  'schedule.availability.editDayTitle': 'Часы работы {date}',
  'schedule.availability.resetDay': 'Вернуть к недельному расписанию',
  'schedule.availability.followsWeekly': 'Этот день следует недельному расписанию.',
  'schedule.availability.differsWeekly': 'Этот день отличается от недельного расписания.',
  'schedule.availability.savePartlyApplied':
    'Часть этого дня сохранена, остальное отклонено. Расписание перечитано, поэтому «Сохранить» запишет только то, чего ещё не хватает.',
  'schedule.availability.closedAllDay': 'Закрыто весь день',
  'schedule.availability.addInterval': 'Добавить часы',
  'schedule.availability.removeInterval': 'Удалить {hours}',
  'schedule.availability.legend': 'Легенда',
  'schedule.availability.legendAvailability': 'Доступность',
  'schedule.availability.legendOccupancy': 'Занятое время',
  'schedule.availability.usually': 'Обычно: {pattern}',
  'schedule.availability.effectOpen': 'Открыто {date}: {hours}',
  'schedule.availability.effectClosed': 'На {date} не остаётся часов работы.',
  'schedule.availability.noEffect': 'Это изменение не меняет часы работы {date}.',
  'schedule.availability.clear': 'Очистить',
  'schedule.availability.restore': 'Восстановить',
  'schedule.availability.presetSunThu': 'Вс–Чт',
  'schedule.availability.presetMonFri': 'Пн–Пт',
  'schedule.availability.presetEveryDay': 'Каждый день',
  'schedule.availability.notConfigured': 'Доступность не настроена.',
  'schedule.availability.createProfile': 'Настройте доступность',
  'schedule.availability.profileSettings': 'Настройки доступности',
  'schedule.availability.settingsHint': 'Часовой пояс и рабочий календарь — собственные настройки планирования {name}, а не опция просмотра',
  'schedule.availability.settingsOwnerNote': 'Эти настройки принадлежат {name}. Часовой пояс и рабочий календарь управляют всем, что здесь запланировано, — не только этим представлением календаря.',
  'schedule.availability.state': 'Состояние',
  'schedule.availability.stateHint': '«Неактивен» приостанавливает весь профиль: он не предлагает часов и недоступен для записи, пока снова не станет активным. Недельное расписание и изменения дат сохраняются.',
  'schedule.availability.days': 'Дни',
  'schedule.availability.followWorkingCalendar': 'Пропускать нерабочие дни выбранного календаря',
  'schedule.availability.day': 'День',
  'schedule.availability.hours': 'Часы',
  'schedule.availability.openEnded': 'Без даты окончания',
  'schedule.availability.reason': 'Причина',
  'schedule.availability.block': 'Недоступно',
  'schedule.availability.extra': 'Дополнительные часы',
  'schedule.availability.start': 'Начало',
  'schedule.availability.end': 'Конец',
  'schedule.availability.validFrom': 'Действительно с',
  'schedule.availability.validTo': 'Действительно по',
  'schedule.availability.addRule': 'Добавить недельные часы',
  'schedule.availability.editRule': 'Изменить недельные часы',
  'schedule.availability.addExtraTitle': 'Добавить часы на дату',
  'schedule.availability.editExtraTitle': 'Изменить добавленные часы',
  'schedule.availability.addBlockTitle': 'Отметить время недоступным',
  'schedule.availability.editBlockTitle': 'Изменить недоступное время',
  'schedule.availability.sunday': 'Воскресенье',
  'schedule.availability.monday': 'Понедельник',
  'schedule.availability.tuesday': 'Вторник',
  'schedule.availability.wednesday': 'Среда',
  'schedule.availability.thursday': 'Четверг',
  'schedule.availability.friday': 'Пятница',
  'schedule.availability.saturday': 'Суббота',
  'schedule.nav.workingCalendar': 'Рабочий календарь',
  'schedule.nav.working-calendarDescription': 'Рабочие и нерабочие дни',

  'schedule.canvas.scaleLabel': 'Масштаб календаря',
  'schedule.canvas.contextMenu': 'Действия календаря',
  'schedule.canvas.scale.years': 'Годы',
  'schedule.canvas.scale.year': 'Год',
  'schedule.canvas.scale.month': 'Месяц',
  'schedule.canvas.scale.week': 'Неделя',
  'schedule.canvas.scale.day': 'День',
  'schedule.canvas.fullDay': 'Целый день',
  'schedule.canvas.nowLine': 'Текущее время',
  'schedule.canvas.nowLineHint': 'Проводить текущее время по часам клиники линией через дорожки.',
  'schedule.canvas.navigation': 'Навигация по календарю',
  'schedule.canvas.todayHint': 'Перейти к сегодня',
  'schedule.canvas.previousDay': 'Предыдущий день',
  'schedule.canvas.nextDay': 'Следующий день',
  // The day on a phone: the two readings, the agenda's name and the picker.
  'schedule.handheld.view': 'Вид',
  'schedule.handheld.agenda': 'Список',
  'schedule.handheld.board': 'Доска',
  'schedule.handheld.agendaLabel': 'Список на {day}',
  'schedule.handheld.agendaEmpty': 'На этот день ничего нет',
  'schedule.handheld.time': '{from}–{to}',
  'schedule.handheld.pickDate': 'Перейти к дате',
  'schedule.canvas.previousWeek': 'Предыдущая неделя',
  'schedule.canvas.nextWeek': 'Следующая неделя',
  'schedule.canvas.previousMonth': 'Предыдущий месяц',
  'schedule.canvas.nextMonth': 'Следующий месяц',
  'schedule.canvas.previousYear': 'Предыдущий год',
  'schedule.canvas.nextYear': 'Следующий год',
  'schedule.canvas.previousYears': 'Предыдущие годы',
  'schedule.canvas.nextYears': 'Следующие годы',
  'schedule.canvas.zoomLabel': 'Детализация времени',
  'schedule.canvas.slotOption': '{minutes} мин',
  'schedule.canvas.zoomHint': 'Размер интервала на временной сетке. Ctrl+Shift+прокрутка тоже меняет его.',
  'schedule.canvas.range': '{from} – {to}',
  'schedule.canvas.bandName': '{from}–{to}, {label}',
  'schedule.canvas.monthGridLabel': 'Дни {month}',
  'schedule.canvas.yearGridLabel': 'Месяцы {year}',
  'schedule.canvas.yearsGridLabel': 'Годы с {from} по {to}',
  'schedule.canvas.weekGridLabel': 'Дни недели',
  'schedule.canvas.dayGridLabel': 'Часы дня',
  'schedule.canvas.intervalTrack': 'Шкала времени за {date}',
  'schedule.canvas.openMonth': 'Открыть {month}',
  'schedule.canvas.openYear': 'Открыть {year}',
  'schedule.canvas.openWeek': 'Открыть неделю',
  'schedule.canvas.openDay': 'Открыть день',

  'schedule.timeObject.selected': 'Выбранный объект',
  'schedule.timeObject.newInterval': 'Новый интервал',
  'schedule.timeObject.chooseType': 'Выберите, что означает этот интервал. Его тип никогда не выводится из фона.',
  'schedule.timeObject.editTitle': 'Изменить объект времени',
  'schedule.timeObject.edit': 'Изменить',
  'schedule.timeObject.duplicate': 'Дублировать',
  'schedule.timeObject.delete': 'Удалить',
  'schedule.timeObject.deleteTitle': 'Удалить объект времени',
  'schedule.timeObject.deleteAvailabilityTitle': 'Удалить этот объект доступности?',
  'schedule.timeObject.deleteAvailabilityDescription': 'Запись отключена и остаётся в истории аудита. Её можно восстановить из раздела «Записи».',
  'schedule.timeObject.type': 'Тип',
  'schedule.timeObject.intervalType': 'Тип интервала',
  'schedule.timeObject.name': 'Название',
  'schedule.timeObject.duration': 'Длительность',
  'schedule.timeObject.recurrence': 'Повтор',
  'schedule.timeObject.recurrence.none': 'Не повторяется',
  'schedule.timeObject.recurrence.daily': 'Каждый день',
  'schedule.timeObject.recurrence.weekly': 'Еженедельно',
  'schedule.timeObject.kind.busy': 'Занятое время',
  'schedule.timeObject.kind.externalAppointment': 'Приём в другом месте',
  'schedule.timeObject.kind.travel': 'Поездка',
  'schedule.timeObject.kind.transfer': 'Трансфер',
  'schedule.timeObject.kind.personal': 'Личное',
  'schedule.timeObject.kind.other': 'Другое обязательство',
  'schedule.timeObject.applyTo': 'Применить это изменение к',
  'schedule.timeObject.scope.occurrence': 'Только к этому событию',
  'schedule.timeObject.scope.following': 'К этому и последующим событиям',
  'schedule.timeObject.scope.series': 'Ко всей будущей серии',
  'schedule.timeObject.ownerTimeZone': 'Гражданское время следует часовому поясу владельца: {zone}.',
  'schedule.timeObject.overlapChooser': 'Здесь объекты перекрываются. Выберите тот, который нужно рассмотреть:',
  'schedule.timeObject.snap': 'Шаг привязки',
  'schedule.timeObject.undo': 'Отменить последнее перемещение',
  'schedule.timeObject.reverted': 'Сервер отклонил изменение. Объект возвращён к подтверждённому времени.',
  'schedule.timeObject.saveFailed': 'Не удалось сохранить объект времени.',
  'schedule.timeObject.clearTitle': 'Очистить редактируемую конфигурацию календаря?',
  'schedule.timeObject.clearDescription': 'Это отключит недельные часы, изменения дат и серии обязательств для этого владельца.',
  'schedule.timeObject.clearRules': 'Правила недельной доступности',
  'schedule.timeObject.clearOverrides': 'Изменения дат',
  'schedule.timeObject.clearSeries': 'Серия обязательств',
  'schedule.timeObject.clearKeepsAppointments': 'Приёмы, резервы и история аудита не удаляются.',
  'schedule.timeObject.copy': 'Копировать',
  'schedule.timeObject.cut': 'Вырезать',
  'schedule.timeObject.paste': 'Вставить сюда',
  'schedule.timeObject.addHere': 'Добавить интервал здесь',
  'schedule.timeObject.newIntervalRange': '{date}, {from}–{to}',
  'schedule.timeObject.clearVisible': 'Очистить показанное',
  'schedule.timeObject.clearVisibleTitle': 'Очистить показанные записи с датами?',
  'schedule.timeObject.clearVisibleRange': '{from} – {to}',
  'schedule.timeObject.clearVisibleConfirm': 'Очистить эти записи',
  'schedule.timeObject.clearVisibleKeeps': 'Недельные часы и повторяющиеся обязательства — постоянные записи, их это не затрагивает. Приёмы, брони и история изменений не удаляются.',
  'schedule.timeObject.clearCommitments': 'Разовые обязательства',
  'schedule.timeObject.clearAll': 'Очистить весь календарь',
  'schedule.timeObject.clearConfirm': 'Очистить конфигурацию',
  'schedule.timePlanner.title': 'Время команды',
  'schedule.timePlanner.subtitle': 'Соберите людей и ресурсы на одной шкале времени и найдите непрерывное окно, доступное всем.',
  'schedule.timePlanner.searchOwners': 'Найти людей или ресурсы',
  'schedule.timePlanner.owners': 'Выбранные календари',
  'schedule.timePlanner.chooseOwners': 'Выберите один или несколько календарей для сравнения.',
  'schedule.timePlanner.loading': 'Загрузка проекции календаря',
  'schedule.timePlanner.loadFailed': 'Не удалось загрузить проекцию календаря.',
  'schedule.timePlanner.lane': 'Объекты времени: {name}',
  'schedule.timePlanner.candidates': 'Взаимно доступное время',
  'schedule.timePlanner.searching': 'Поиск общего времени',
  'schedule.timePlanner.searchFailed': 'Не удалось рассчитать общее время.',
  'schedule.timePlanner.noCandidates': 'В этот день нет непрерывного окна, подходящего всем выбранным календарям.',
  'schedule.timePlanner.object.availability': 'Доступно',
  'schedule.timePlanner.object.override': 'Изменение даты',
  'schedule.timePlanner.object.commitment': 'Обязательство',
  'schedule.timePlanner.object.appointment': 'Приём',
  'schedule.timePlanner.object.hold': 'Резерв',
  'schedule.timePlanner.object.constraint': 'Вне периода поездки',

  'schedule.workingCalendar.title': 'Рабочий календарь',
  'schedule.workingCalendar.subtitle': 'Рабочие дни страны или региона на любом масштабе — от лет до часов.',
  'schedule.workingCalendar.calendar': 'Календарь',
  'schedule.workingCalendar.loading': 'Загрузка рабочего календаря',
  'schedule.workingCalendar.empty': 'Рабочие календари не импортированы.',
  'schedule.workingCalendar.emptyDetail': 'Импортируйте календарь страны, прежде чем настраивать по нему рабочие часы клиники или ресурса.',
  'schedule.workingCalendar.sourceNotice': 'Загружено из {source} ({version}). Ручные изменения дней сохраняются в расписании.',
  'schedule.workingCalendar.readOnlyNotice': 'Вы можете просматривать этот календарь, но не можете изменять дни уровня страны.',
  'schedule.workingCalendar.status.working': 'Рабочий день',
  'schedule.workingCalendar.status.short': 'Короткий день',
  'schedule.workingCalendar.status.nonWorking': 'Нерабочий день',
  'schedule.workingCalendar.weekend': 'Выходной',
  'schedule.workingCalendar.legend': 'Легенда',
  'schedule.workingCalendar.legendMarked': 'Задано источником или ручной правкой',
  'schedule.workingCalendar.daySummary': '{date}: {status}',
  'schedule.workingCalendar.daySummaryWithReason': '{date}: {status} — {reason}',
  'schedule.workingCalendar.monthSummary': 'Рабочих дней: {working}, выходных: {off}',
  'schedule.workingCalendar.markedCount': 'Отмеченных дней: {count}',
  'schedule.workingCalendar.status': 'Статус',
  'schedule.workingCalendar.reason': 'Причина',
  'schedule.workingCalendar.reasonAction': 'Причина…',
  'schedule.workingCalendar.reasonTitle': 'Почему этот день отличается',
  'schedule.workingCalendar.cancel': 'Отмена',
  'schedule.workingCalendar.saveFailed': 'Не удалось сохранить день рабочего календаря.',
  'schedule.visitTypes.open': 'Управление типами приёмов в расписании',

  'patientSchedule.title': 'Доступность пациента',
  'patientSchedule.subtitle': 'Обязательства вне этой клиники участвуют в каждой проверке конфликтов.',
  'patientSchedule.start': 'Начало',
  'patientSchedule.end': 'Конец',
  'patientSchedule.empty': 'Недоступное время не зафиксировано.',

  'schedule.board.loading': 'Загрузка доски',
  'schedule.board.loadFailed': 'Не удалось загрузить доску',
  'schedule.board.empty': 'Нет дорожек для показа',
  'schedule.board.emptyDetail': 'На этот день никакие ресурсы не настроены.',
  'schedule.board.legendLabel': 'Легенда',
  'schedule.board.legendBuffer': 'Буфер',
  'schedule.board.legendLayers': 'Слои',
  'schedule.board.legendActual': 'Фактическое время',
  'schedule.board.legendNow': 'Сейчас',
  'schedule.legend.available': 'Рабочие часы владельца, под всем остальным на дорожке: приёмы записываются поверх них.',
  'schedule.legend.extra': 'Часы, добавленные к обычным на этот день; нарисованы как рабочие, но синим.',
  'schedule.legend.block': 'Часы, убранные из обычных, — красная штриховка: на них ничего не записывается.',
  'schedule.legend.appointment': 'Записанный приём: заполненная карточка на дорожке с пациентом и статусом.',
  'schedule.legend.hold': 'Время, зарезервированное под ещё не подтверждённый приём: пунктирный контур без заливки.',
  'schedule.legend.busy': 'Владелец занят в другом месте: серая карточка — время занято без приёма здесь.',
  'schedule.legend.blockedTime': 'Время, заблокированное на этой дорожке: красная карточка с причиной.',
  'schedule.legend.mutual': 'Все выбранные в планировщике свободны здесь: пунктирный коридор через все дорожки.',
  'schedule.legend.patientBusy': 'У выбранного пациента в это время что-то другое: фиолетовая штриховка через дорожки.',
  'schedule.legend.buffer': 'Время на подготовку и уборку, которое ресурс резервирует вокруг приёма: точечные края за карточкой.',
  'schedule.legend.actual': 'Когда начатый приём шёл на самом деле: полоска вдоль начального края дорожки.',
  'schedule.legend.now': 'Часы клиники прямо сейчас: линия через все дорожки с временем на оси.',
  'schedule.board.mode': 'Вид',
  'schedule.board.modeReception': 'Регистратура',
  'schedule.board.modeCanvas': 'Холст',
  'schedule.board.modeHint': 'Регистратура: колонка на ресурс, слоты выбранного шага одной высоты, карточка на запись. Холст: свободные дорожки с любым масштабом.',
  'schedule.board.legendShow': 'Показать легенду',
  'schedule.board.legendHide': 'Скрыть легенду',
  'schedule.reception.label': 'Регистратурная сетка',
  'schedule.reception.slot': 'Свободно в {time}',
  'schedule.reception.arrived': 'На месте',
  'schedule.reception.inProgress': 'На приёме',
  'schedule.reception.done': 'Завершено',
  'schedule.reception.late': 'Опоздание',
  'schedule.reception.lateBy': 'Опоздание {minutes} мин',
  'schedule.reception.open': 'Приёмные часы',
  'schedule.reception.closed': 'Закрыто',
  'schedule.reception.legendPayment': 'Категория оплаты',
  'schedule.reception.legendTime': 'Время',
  'schedule.reception.legendPlanned': 'Запись, по которой пациент ещё не отмечен: пациент жирным, под именем тип визита и длительность, без отметки у края.',
  'schedule.reception.legendArrived': 'Пациент в клинике: зелёная галочка у края карточки.',
  'schedule.reception.legendInProgress': 'Приём начался: знак пульса.',
  'schedule.reception.legendDone': 'Приём завершён: отмеченный календарь, карточка приглушена.',
  'schedule.reception.legendLate': 'Часы клиники прошли начало, а пациент не отмечен: красный знак часов, карточка говорит, на сколько минут.',
  'schedule.reception.legendNoShow': 'Пациент не пришёл: красный перечёркнутый знак и зачёркнутое имя.',
  'schedule.reception.legendStripe': 'Полоса у начального края карточки — цвет типа визита, один и тот же для типа в каждой колонке.',
  'schedule.reception.legendOpen': 'Ресурс принимает: белые ячейки, каждая свободная с временем. Нажатие на ячейку записывает в этот слот.',
  'schedule.reception.legendClosed': 'Вне часов ресурса: серые ячейки без времени, записать нельзя.',
  'schedule.reception.legendBlock': 'Часы, закрытые вручную внутри смены: красная штриховка с причиной.',
  'schedule.board.conflict': 'Конфликт в расписании',
  'schedule.board.patientUnavailable': 'Пациент недоступен',
  'schedule.board.holdUntil': 'Резерв до {time}',
  'schedule.board.holdExpiredBadge': 'Срок резерва истёк',
  'schedule.board.lockedEvent': 'Это событие больше нельзя перенести.',
  'schedule.board.time': 'Время',
  'schedule.board.resource': 'Ресурс',
  'schedule.board.visitType': 'Тип визита',
  'schedule.board.start': 'Начало',
  'schedule.board.end': 'Конец',
  'schedule.board.snapHint': 'Перетаскивание, стрелки и ввод времени двигают с этим шагом.',
  'schedule.board.pasteAt': 'Вставить в {time}',
  'schedule.board.copied': 'Скопировано: {name}. Щёлкните правой кнопкой по дорожке, чтобы вставить туда.',
  'schedule.board.pasteRefused': 'Эту запись нельзя скопировать сюда: на этой доске не видно того, что нужно для копии.',
  'schedule.board.cancelTitle': 'Отменить этот приём?',
  'schedule.board.cancelBlockTitle': 'Убрать эту блокировку?',
  'schedule.board.cancelDescription': 'Запись уйдёт с доски. История аудита сохранится.',
  'schedule.board.keep': 'Оставить',
  'schedule.board.createTitle': 'Новое в расписании',
  'schedule.board.createKind': 'Что создать',
  'schedule.board.createRange': '{date}, {from}–{to}',
  'schedule.board.createAt': '{date}, {time}',
  'schedule.board.add': 'Добавить приём',
  'schedule.planner.hide': 'Скрыть планировщик',
  'schedule.bandLabel': '{label}: {detail}',
  'schedule.calendar.lanesLabel': 'Доска дня по ресурсам',
  'schedule.calendar.openEvent': 'Открыть событие',
  'schedule.calendar.appointmentHere': 'Новый приём в {time}',
  'schedule.calendar.blockHere': 'Заблокировать время в {time}',
  'schedule.calendar.emptySelection': 'У выбранных участников нет дорожек для записи в этот день.',
  'schedule.calendar.dayNote': 'Приёмов: {appointments}',
  'schedule.calendar.dayNoteConflicts': 'Приёмов: {appointments} · конфликтов: {conflicts}',
  'schedule.calendar.busyOwner': '{owner}, {what}',
  'schedule.calendar.dayLabel': '{day}, {note}',
  'schedule.planner.title': 'Совместное планирование',
  'schedule.planner.objectTitle': '{from}–{to}, {name}',
  'schedule.planner.hint': 'Выберите людей, кабинеты и оборудование, чтобы наложить их время на календарь и найти общие свободные окна.',
  'schedule.planner.selected': 'Выбранные участники',
  'schedule.planner.remove': 'Убрать {name}',
  'schedule.planner.clear': 'Очистить всё',
  'schedule.planner.search': 'Поиск людей и ресурсов',
  'schedule.planner.typeFilter': 'Фильтр по типу',
  'schedule.planner.loading': 'Загрузка каталога',
  'schedule.planner.loadFailed': 'Не удалось загрузить каталог',
  'schedule.planner.noMatches': 'По этому запросу ничего не найдено.',
  'schedule.planner.capReached': 'Одновременно можно наложить до {count} участников.',
  'schedule.planner.chooseOwners': 'Выберите хотя бы одного участника, чтобы увидеть общее свободное время.',
  'schedule.planner.monthHint': 'Общие свободные окна показываются в видах недели и дня.',
  'schedule.planner.windows': 'Общее свободное время',
  'schedule.planner.searching': 'Поиск общего времени',
  'schedule.planner.searchFailed': 'Не удалось выполнить поиск общего времени',
  'schedule.planner.blocked': 'Нет общего свободного времени у: {names}',
  'schedule.planner.noWindows': 'В этом диапазоне нет общих свободных окон.',
  'schedule.planner.book': 'Записать',
  'schedule.planner.mutual': 'Все свободны',
  'schedule.planner.projectionFailed': 'Не удалось загрузить календари участников',
  'schedule.find.patient': 'Пациент',
  'schedule.find.type': 'Тип приёма',
  'schedule.find.date': 'День для поиска',
  'schedule.find.choose': 'Выберите…',
  'schedule.find.requirements': 'Требования',
  'schedule.find.anyResource': 'Любой подходящий ресурс',
  'schedule.find.missingInput': 'Выберите пациента и тип приёма и укажите причину визита для поиска.',
  'schedule.find.patientAvailability': 'Доступность пациента',
  'schedule.find.searching': 'Поиск свободных окон',
  'schedule.find.failed': 'Не удалось завершить поиск',
  'schedule.find.empty': 'Нет подходящих окон',
  'schedule.find.emptyDetail': 'Попробуйте другой день или ослабьте требования к ресурсам.',
  'schedule.find.candidates': 'Рекомендуемое время',
  'schedule.find.hold': 'Зарезервировать {time}',
  'schedule.requirement.required': 'Обязательный',
  'schedule.requirement.preferred': 'Предпочтительный',
  'schedule.requirement.contextOnly': 'Только контекст',
  'schedule.panel.title': 'Элемент расписания',
  'schedule.panel.loading': 'Загрузка события',
  'schedule.panel.loadFailed': 'Не удалось загрузить событие',
  'schedule.panel.planned': 'Запланировано',
  'schedule.panel.actual': 'Фактически',
  'schedule.panel.arrived': 'Прибыл {time}',
  'schedule.panel.markArrived': 'Отметить прибытие',
  'schedule.panel.reservations': 'Зарезервированные ресурсы',
  'schedule.panel.buffers': 'Буферы {pre} + {post} мин',
  'schedule.panel.reservationReleased': 'Снято',
  'schedule.panel.holdExpires': 'Срок резерва истекает в {time}',
  'schedule.panel.newStart': 'Новое время начала',
  'schedule.panel.save': 'Сохранить новое время',
  'schedule.panel.cancelEvent': 'Отменить приём',
  'schedule.panel.cancelBlock': 'Отменить блокировку',
  'schedule.panel.cancelReason': 'Причина отмены',
  'schedule.panel.confirmHold': 'Подтвердить резерв',
  'schedule.panel.releaseHold': 'Снять резерв',
  'schedule.atRisk.resourceBlocked': 'Ресурс заблокирован',
  'schedule.atRisk.resourceInactive': 'Ресурс неактивен',
  'schedule.atRisk.availabilityChanged': 'Доступность изменилась',
  'schedule.atRisk.capacityConflict': 'Конфликт ёмкости',
  'schedule.reception.payment': 'Оплата',
  'schedule.reception.payment.personal': 'За свой счёт',
  'schedule.reception.payment.insurance': 'Страховка',
  'schedule.reception.payment.program': 'Программа',
  'schedule.reception.payment.unspecified': 'Не указано',
  'schedule.reception.paymentHint': 'Что пациент сказал об оплате — пометка для регистратуры. Она не задаёт цену, не проверяет покрытие, не фиксирует долг и ничего не отмечает оплаченным.',
  'schedule.reception.comment': 'Комментарий к записи',
  'schedule.reception.checked': 'Проверено',
  'schedule.reception.confirmed': 'Подтверждено',
  'schedule.reception.contactCompleted': 'Связь состоялась',
  'schedule.reception.wantsAnotherTime': 'Хочет другое время',
  'schedule.reception.minor': 'Несовершеннолетний',
  'schedule.reception.debt': 'Есть долг перед клиникой',
  'schedule.reception.hasComment': 'Есть комментарий',
  'schedule.impact.foundAt': 'Конфликт найден проверкой {time}',
  'schedule.impact.detail': '{reasons} — найдено последней автоматической проверкой {time}. С тех пор визит не перепроверялся; его собственный перенос или отмена снимает отметку.',
  'schedule.finance.title': 'Счёт',
  'schedule.finance.receivable': 'К оплате',
  'schedule.finance.availableFunds': 'Доступные средства',
  'schedule.finance.prepaidValue': 'Предоплачено',
  'schedule.finance.asOf': 'На {time}',
  'schedule.finance.noAccount': 'Собственного счёта нет',
  'schedule.finance.unavailable': 'Финансовые данные недоступны',
  'schedule.finance.loading': 'Читаем счёт',
  'schedule.finance.open': 'Открыть в Финансах',
  'schedule.finance.noVisitClaim': 'Это данные счёта, а не утверждение об оплате этого визита.',
  'schedule.finance.openAccount': 'Открыть счёт',
  'schedule.finance.packages': 'Пакеты',
  'schedule.finance.packagesLoading': 'Читаем пакеты',
  'schedule.finance.packagesUnavailable': 'Пакеты недоступны',
  'schedule.finance.packagesUndetermined': 'Пакеты не определены: ни один собственный счёт не называет персону',
  'schedule.finance.packagesNone': 'Активных пакетов нет',
  'schedule.finance.packagePurchased': 'Куплен',
  'schedule.finance.packageRemaining': 'Остаток',
  'schedule.finance.packageConsumed': 'Использовано',
  'schedule.finance.packagesMore': 'Остальные пакеты перечислены в Финансах.',
  'schedule.finance.openPackages': 'Открыть предоплаченные пакеты',
  'schedule.onlineBooking.title': 'Онлайн-запись',
  'schedule.onlineBooking.loading': 'Читаем предложения',
  'schedule.onlineBooking.undetermined': 'Не определено',
  'schedule.onlineBooking.none': 'Не предлагается ни для одного активного типа визита',
  'schedule.onlineBooking.offeredFor': 'Предлагается для: {types}',
  'schedule.onlineBooking.factLoading': 'Онлайн-запись на этот тип визита к этому специалисту: читаем предложения',
  'schedule.onlineBooking.factOffered': 'Онлайн-запись на этот тип визита к этому специалисту предлагается',
  'schedule.onlineBooking.factNotOffered': 'Онлайн-запись на этот тип визита к этому специалисту не предлагается',
  'schedule.onlineBooking.factUnknown': 'Онлайн-запись на этот тип визита к этому специалисту не определена',
  'schedule.combinations.title': 'Комбинация',
  'schedule.combinations.none': 'Без комбинации',
  'schedule.combinations.manage': 'Комбинации',
  'schedule.combinations.panelTitle': 'Комбинации ресурсов',
  'schedule.combinations.intro': 'Специалист, кабинет и оборудование, которые бронируют вместе. Комбинация сужает доску до своих участников и подставляет их в новый визит; каждое бронирование по-прежнему проверяется полностью.',
  'schedule.combinations.saved': 'Сохранённые комбинации',
  'schedule.combinations.empty': 'Комбинаций пока нет',
  'schedule.combinations.use': 'Использовать',
  'schedule.combinations.inUse': 'Используется',
  'schedule.combinations.remove': 'Удалить комбинацию {name}',
  'schedule.combinations.new': 'Новая комбинация',
  'schedule.combinations.name': 'Название',
  'schedule.combinations.noMember': 'Нет',
  'schedule.combinations.needTwo': 'Назовите комбинацию и выберите не менее двух участников.',
  'schedule.combinations.limit': 'Можно сохранить не более {max} комбинаций.',
  'schedule.combinations.save': 'Сохранить комбинацию',
  'schedule.combinations.unknownResource': 'Ресурса больше нет в каталоге',
  'schedule.combinations.unsaved': 'Новая комбинация ресурсов',
  'schedule.combinations.loadFailed': 'Сохранённые комбинации не удалось прочитать.',
  'schedule.reception.legendMarks': 'Отметки визита',
  'schedule.reception.legendPayPersonal': 'Пациент сказал, что платит сам: зелёная карточка.',
  'schedule.reception.legendPayInsurance': 'Пациент назвал страховую: синяя карточка.',
  'schedule.reception.legendPayProgram': 'Визит по программе: фиолетовая карточка.',
  'schedule.reception.legendPayUnspecified': 'Пока ничего не указано: серая карточка. Заливка — слова пациента, а не цена, покрытие или оплата.',
  'schedule.reception.legendConflict': 'Последняя автоматическая проверка нашла конфликт у этого визита: красный знак предупреждения, карточка говорит, когда он найден.',
  'schedule.reception.legendDebt': 'Счёт пациента должен клинике: янтарный знак монет. Факт счёта, а не этого визита.',
  'schedule.reception.legendMinor': 'Пациенту меньше восемнадцати на календарный день: отметка <18.',
  'schedule.reception.legendComment': 'Регистратура оставила комментарий к визиту: знак реплики.',
  'schedule.reception.legendContact': 'С пациентом связались по этому визиту: зелёная трубка.',
  'schedule.reception.legendAnotherTime': 'Пациент попросил другое время: янтарный знак календаря.',
  'schedule.reception.minorMark': '<18',
  'schedule.reception.state': 'Статус',
  'schedule.reception.resources': 'Ресурсы',
  'schedule.reception.resourceInfo': 'О {name}',
  'schedule.reception.workplaces': 'Рабочие места',
  'schedule.reception.hours': 'Часы сегодня',
  'schedule.reception.noHours': 'Сегодня смены нет',
  'schedule.reception.load': 'Загрузка',
  'schedule.reception.loadValue': '{booked} из {available} мин, {percent}%',
  'schedule.reception.visits': 'Визиты',
  'schedule.reception.moveEarlier': 'Сдвинуть колонку раньше',
  'schedule.reception.moveLater': 'Сдвинуть колонку позже',
  'schedule.reception.weekLabel': 'Неделя регистратуры',
  'schedule.reception.weekCell': '{resource}, {day}: визитов {visits}',
  'schedule.reception.weekLoad': '{booked} из {available} мин',
  'schedule.reception.weekVisits': 'Визитов: {count}',
  'schedule.reception.weekConflicts': 'Конфликтов: {count}',
  'schedule.reception.weekPercent': '{percent}%',
  'schedule.reception.filters': 'Фильтры доски',
  'schedule.reception.specialty': 'Специальность',
  'schedule.reception.specialtyAll': 'Все специальности',
  'schedule.reception.search': 'Поиск ресурсов',
  'schedule.reception.typesAll': 'Все типы',
  'schedule.board.patientChart': 'Карта пациента',
  'schedule.board.patientSchedule': 'Расписание пациента',
  'schedule.board.moveStart': 'Перенести',
  'schedule.board.moving': 'Переносится {name}. Правый клик по слоту поставит визит туда.',
  'schedule.board.moveAt': 'Перенести сюда, {time}',
  'schedule.board.clipCancel': 'Сбросить',
  'schedule.board.toWaiting': 'Добавить в лист ожидания',
}
